import {
  Box,
  Button,
  FormField,
  Icon,
  Input,
  Modal,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-core';
import React, { ReactNode } from 'react';
import { useState } from 'react';
import { DragHandle } from './SortableCollectionEpics';
import CollectionEpicWrapper from './CollectionEpicWrapper';
import CollectionStoryWithIndex from './CollectionStoryWithIndex';
import { CollectionStory, ContentType } from '@amzn/awscat-pattern-library-service-model';
import constants from '../../common/constants';
import StoryPublication from './StoryPublication';
import { MAX_TITLE_LENGTH, MAX_DESCRIPTION_LENGTH, reorderCollectionStories } from './utils';
import '../../resources/styles/scss/components/collection/collection.scss';
import { EDIT_COLLECTION_CHAR_LIMIT } from '../../api/UtilsApi';
import { SortableList, SortableRenderArgs } from '../v2-common/SortableList';

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfStory = (story: CollectionStoryWithIndex) => story.id;

interface StoryRowProps {
  readonly epics: CollectionEpicWrapper[];
  readonly setEpics: (epics: CollectionEpicWrapper[]) => void;
  readonly epic: CollectionEpicWrapper;
  readonly epicNumber: number;
  readonly item: CollectionStoryWithIndex;
  readonly setNodeRef: SortableRenderArgs['setNodeRef'];
  readonly style: SortableRenderArgs['style'];
}

// One story table row; the drag handle in the last cell binds to this row's SortableList item.
function StoryRow({ epics, setEpics, epic, epicNumber, item, setNodeRef, style }: StoryRowProps) {
  const [deleteStoryModalVisible, setDeleteStoryModalVisible] = useState<boolean>(false);

  const handleDeleteStory = () => {
    // Replace the stories array so the SortableList sees the removal.
    epic.stories = epic.stories.filter((story) => story.id !== item.id);

    // Renumber so `index` keeps matching each story's position.
    epic.stories.forEach((story, index) => {
      story.index = index;
    });
    setEpics([...epics]);
    setDeleteStoryModalVisible(false);
  };

  const handleStoryDescriptionChange = (newDescr: string) => {
    item.story.description = newDescr;
    setEpics([...epics]);
  };

  const handleStoryTitleChange = (newTitle: string) => {
    item.story.title = newTitle;
    setEpics([...epics]);
  };

  // The service model marks these optional; a new story starts with empty strings.
  const title = item.story.title ?? '';
  const description = item.story.description ?? '';
  const charRemainingTitle =
    MAX_TITLE_LENGTH - title.length + constants.EDIT_COLLECTION_CHAR_LIMIT_TEXT;
  const charRemainingDescr =
    MAX_DESCRIPTION_LENGTH - description.length + constants.EDIT_COLLECTION_CHAR_LIMIT_TEXT;
  return (
    <tr ref={setNodeRef} style={style} className="awsui-table-row task-list-table-row ">
      <td>
        <span>
          <span>
            <div>
              <FormField
                errorText={
                  title.length > EDIT_COLLECTION_CHAR_LIMIT &&
                  constants.COLLECTION_INPUT_CHARACTER_LIMIT_ERROR
                }
                constraintText={
                  title.length > EDIT_COLLECTION_CHAR_LIMIT ? (
                    <></>
                  ) : (
                    <Box color="text-body-secondary">{charRemainingTitle}</Box>
                  )
                }
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
              >
                <Textarea
                  placeholder={constants.EDIT_COLLECTION_TITLE_PLACEHOLDER}
                  onChange={(event) => handleStoryTitleChange(event.detail.value)}
                  value={title}
                  invalid={title.length > EDIT_COLLECTION_CHAR_LIMIT}
                  ariaRequired
                />
              </FormField>
            </div>
          </span>
        </span>
      </td>
      <td>
        <span>
          <span>
            <div>
              <FormField
                errorText={
                  description.length > EDIT_COLLECTION_CHAR_LIMIT &&
                  constants.COLLECTION_INPUT_CHARACTER_LIMIT_ERROR
                }
                constraintText={
                  description.length > EDIT_COLLECTION_CHAR_LIMIT ? (
                    <></>
                  ) : (
                    <Box color="text-body-secondary">{charRemainingDescr}</Box>
                  )
                }
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
              >
                <Textarea
                  placeholder={constants.EDIT_COLLECTION_DESCRIPTION_PLACEHOLDER}
                  onChange={(event) => handleStoryDescriptionChange(event.detail.value)}
                  value={description}
                  ariaRequired
                />
              </FormField>
            </div>
          </span>
        </span>
      </td>
      <td>
        <span>
          <span>
            <StoryPublication
              item={item}
              epicNumber={epicNumber}
              epics={epics}
              setEpics={setEpics}
            />
          </span>
        </span>
      </td>
      <td>
        <span>
          <span>
            <SpaceBetween direction="horizontal" size="xxs">
              <Modal
                visible={deleteStoryModalVisible}
                onDismiss={() => setDeleteStoryModalVisible(false)}
                header={constants.DELETE_COLLECTION_STORY_HEADER_TEXT}
                closeAriaLabel="Close modal"
                footer={
                  <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button variant="link" onClick={() => setDeleteStoryModalVisible(false)}>
                        No
                      </Button>
                      <Button variant="primary" onClick={handleDeleteStory}>
                        Yes
                      </Button>
                    </SpaceBetween>
                  </Box>
                }
              >
                {constants.DELETE_COLLECTION_STORY_MODAL_TEXT}
              </Modal>
              <button
                onClick={() => setDeleteStoryModalVisible(true)}
                className="edit-collection-button"
                disabled={epic.stories.length <= 1}
              >
                <Icon size="medium" name="remove" />
              </button>
              <DragHandle />
            </SpaceBetween>
          </span>
        </span>
      </td>
    </tr>
  );
}

interface SortableUserStoriesProps {
  readonly epics: CollectionEpicWrapper[];
  readonly setEpics: (epics: CollectionEpicWrapper[]) => void;
  readonly epic: CollectionEpicWrapper;
  readonly epicNumber: number;
}

/** Reorderable user-story table for one collection epic. */
export function SortableUserStories({
  epics,
  setEpics,
  epic,
  epicNumber,
}: SortableUserStoriesProps) {
  const { stories } = epic;

  const handleAddStory = () => {
    // Replace the stories array so the SortableList sees the addition.
    epic.stories = [
      ...stories,
      new CollectionStoryWithIndex(
        CollectionStory.create('', '', '', '', ContentType.Pattern),
        stories.length,
      ),
    ];
    setEpics([...epics]);
  };

  const handleReorder = (newOrderIds: readonly string[]) => {
    setEpics(reorderCollectionStories(epics, epic.id, newOrderIds));
  };

  const renderStory = (
    story: CollectionStoryWithIndex,
    { setNodeRef, style }: SortableRenderArgs,
  ) => (
    <StoryRow
      epics={epics}
      setEpics={setEpics}
      epic={epic}
      epicNumber={epicNumber}
      item={story}
      setNodeRef={setNodeRef}
      style={style}
    />
  );

  const storyTableHeader = '(' + stories.length + ')';

  // The table markup wraps the rows so each <tr> stays a direct child of <tbody>.
  const renderTable = (rows: ReactNode) => (
    <Box margin={{ bottom: 'l' }}>
      <awsui-table initialized="true">
        <div className="task-list-table awsui-table-inner awsui-table-variant-default edit-collection-epic-container">
          <div className="awsui-table-container">
            <table>
              <colgroup span={4}>
                <col width={200} />
                <col width={200} />
                <col width={540} />
                <col width={80} />
              </colgroup>
              <thead>
                <tr>
                  <th colSpan={4} className="collection-story-table-header">
                    <div className="display-flex">
                      <Box
                        fontSize="heading-l"
                        fontWeight="bold"
                        padding={{ top: 'm', left: 'm', bottom: 'm', right: 'xs' }}
                      >
                        User stories
                      </Box>
                      <Box
                        color="text-body-secondary"
                        fontSize="heading-l"
                        padding={{ top: 'm', bottom: 'm' }}
                      >
                        {storyTableHeader}
                      </Box>
                      <div className="edit-collection-add-story-button">
                        <Button iconName="add-plus" onClick={handleAddStory}>
                          {constants.ADD_USER_STORY_BUTTON_TEXT}
                        </Button>
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th>
                    <span className="awsui-table-header-content">
                      {constants.EDIT_COLLECTION_TITLE_TABLE_HEADER}
                    </span>
                  </th>
                  <th>
                    <span className="awsui-table-header-content">
                      {constants.EDIT_COLLECTION_DESCRIPTION_HEADER}
                    </span>
                  </th>
                  <th>
                    <span className="awsui-table-header-content">
                      <Box fontWeight="bold" variant="awsui-key-label">
                        {constants.EDIT_COLLECTION_GUIDANCE_TABLE_HEADER}
                      </Box>
                      <Box variant="awsui-key-label" fontSize="body-s">
                        {constants.EDIT_COLLECTION_GUIDANCE_TABLE_DESCRIPTION}
                      </Box>
                    </span>
                  </th>
                  <th>
                    <span className="awsui-table-header-content" />
                  </th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        </div>
      </awsui-table>
    </Box>
  );

  return (
    <SortableList
      id={`collection-stories-${epic.id}`}
      items={stories}
      keyExtractor={keyOfStory}
      onReorder={handleReorder}
      isDisabled={stories.length <= 1}
      container={renderTable}
      item={renderStory}
    />
  );
}
